// EQS (equity screener) and RV (peers), both on Nasdaq's US listing.

import { get, quotes, type Quote } from "../api";
import { button, clear, errorBox, h, loading, toggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

interface Listing {
  symbol: string;
  name: string;
  last: number | null;
  chg: number | null;
  pct: number | null;
  volume: number | null;
  mcap: number | null;
  country: string;
  ipo: number | null;
  sector: string;
  industry: string;
}

// --- EQS --------------------------------------------------------------------

interface Fields {
  total: number;
  sectors: [string, number][];
  industries: [string, string, number][];
  countries: [string, number][];
}

interface Screen {
  total: number;
  matched: number;
  rows: Listing[];
}

interface Filters {
  sector: string;
  industry: string;
  country: string;
  cap: string;
  pmin: string;
  pmax: string;
  chgmin: string;
  chgmax: string;
  vmin: string;
  sort: string;
  dir: "asc" | "desc";
  limit: string;
}

const DEFAULTS: Filters = {
  sector: "",
  industry: "",
  country: "",
  cap: "",
  pmin: "",
  pmax: "",
  chgmin: "",
  chgmax: "",
  vmin: "",
  sort: "mcap",
  dir: "desc",
  limit: "50",
};

const PREFS = "berg.eqs";
function loadFilters(): Filters {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREFS) ?? "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

const CAPS: [string, string][] = [
  ["", "Any"],
  ["mega", "Mega >200B"],
  ["large", "Large 10-200B"],
  ["mid", "Mid 2-10B"],
  ["small", "Small 0.3-2B"],
  ["micro", "Micro 50-300M"],
  ["nano", "Nano <50M"],
];

const SORTS: [string, string][] = [
  ["mcap", "Market cap"],
  ["pct", "% change"],
  ["chg", "Net change"],
  ["volume", "Volume"],
  ["last", "Price"],
  ["symbol", "Ticker"],
  ["name", "Name"],
  ["industry", "Industry"],
  ["country", "Country"],
  ["ipo", "IPO year"],
];

function runEqs(ctx: Ctx): void {
  const flt = loadFilters();
  const save = () => localStorage.setItem(PREFS, JSON.stringify(flt));
  const controls = h("div", { class: "toolbar" });
  const caps = h("div", { class: "toolbar" });
  const ranges = h("div", { class: "toolbar" });
  const counts = h("div", { class: "pad" }, loading("Loading the US listing"));
  const t = new DataTable<Listing>(
    [
      { label: "Ticker", get: (r) => r.symbol, cls: () => "amber" },
      { label: "Name", get: (r) => r.name, cls: () => "name" },
      { label: "Last", get: (r) => r.last, fmt: (v) => f.price(v), num: true },
      { label: "Net chg", get: (r) => r.chg, fmt: (v) => f.chg(v), cls: (v) => f.cls(v), num: true },
      { label: "%Chg", get: (r) => r.pct, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true },
      { label: "Volume", get: (r) => r.volume, fmt: (v) => f.big(v), num: true },
      { label: "Mkt cap", get: (r) => r.mcap, fmt: (v) => f.big(v), num: true },
      { label: "Industry", get: (r) => r.industry || null, cls: () => "name dim" },
      { label: "Country", get: (r) => r.country || null, cls: () => "dim" },
      { label: "IPO", get: (r) => r.ipo, cls: () => "dim", num: true },
    ],
    { panel: ctx.panel, sortable: false, action: (r) => `${r.symbol} DES` },
  );
  clear(ctx.body, controls, caps, ranges, counts, t.el);

  let fields: Fields | null = null;
  let seq = 0;
  let timer = 0;

  const query = async () => {
    const mine = ++seq;
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(flt)) if (v !== "") params[k] = v;
    try {
      const r = await get<Screen>("eqs", params, ctx.signal);
      if (mine !== seq || ctx.signal.aborted) return;
      const sortLabel = SORTS.find((s) => s[0] === flt.sort)?.[1] ?? flt.sort;
      clear(
        counts,
        h("span", { class: "amber" }, f.int(r.data.matched)),
        ` of ${f.int(r.data.total)} US-listed stocks match`,
        r.data.matched > r.data.rows.length ? ` · top ${r.data.rows.length} by ${sortLabel.toLowerCase()}` : "",
        h("span", { class: "dim" }, "  ·  prices from the hourly Nasdaq listing; click a row or type its number for DES"),
      );
      t.set(r.data.rows);
      ctx.panel.status("Nasdaq listing · refreshed hourly", r.meta);
    } catch (e) {
      if (mine === seq && !ctx.signal.aborted) clear(counts, errorBox(e));
    }
  };
  const changed = (debounce = 0) => {
    save();
    clearTimeout(timer);
    timer = window.setTimeout(query, debounce);
  };

  const select = (key: "sector" | "industry" | "country", options: [string, string][], all: string) => {
    const el = h(
      "select",
      {
        style: { maxWidth: "26ch" },
        onchange: () => {
          flt[key] = el.value;
          if (key === "sector") flt.industry = "";
          drawControls();
          changed();
        },
      },
      h("option", { value: "" }, all),
      ...options.map(([v, label]) => h("option", { value: v, selected: v === flt[key] ? "" : undefined }, label)),
    );
    return el;
  };
  const input = (key: "pmin" | "pmax" | "chgmin" | "chgmax" | "vmin", placeholder: string, width = "7ch") =>
    h("input", {
      value: flt[key],
      placeholder,
      inputmode: "decimal",
      style: { width },
      oninput: (e: Event) => {
        flt[key] = (e.target as HTMLInputElement).value.trim();
        changed(500);
      },
    });

  const drawControls = () => {
    if (!fields) return;
    const inSector = fields.industries.filter(([s]) => !flt.sector || s === flt.sector);
    const industries = new Map<string, number>();
    for (const [, ind, n] of inSector) industries.set(ind, (industries.get(ind) ?? 0) + n);
    const sortSel = h(
      "select",
      {
        onchange: () => {
          flt.sort = sortSel.value;
          changed();
        },
      },
      ...SORTS.map(([v, label]) => h("option", { value: v, selected: v === flt.sort ? "" : undefined }, label)),
    );
    const limitSel = h(
      "select",
      {
        onchange: () => {
          flt.limit = limitSel.value;
          changed();
        },
      },
      ...["25", "50", "100", "200"].map((n) => h("option", { value: n, selected: n === flt.limit ? "" : undefined }, n)),
    );
    clear(
      controls,
      h("label", null, "Sector"),
      select("sector", fields.sectors.map(([s, n]) => [s, `${s} (${n})`]), "All sectors"),
      h("label", null, "Industry"),
      select(
        "industry",
        [...industries].sort((a, b) => a[0].localeCompare(b[0])).map(([s, n]) => [s, `${s} (${n})`]),
        "All industries",
      ),
      h("label", null, "Country"),
      select("country", fields.countries.map(([s, n]) => [s, `${s} (${n})`]), "All countries"),
    );
    clear(
      caps,
      h("label", null, "Mkt cap"),
      ...toggles(CAPS, flt.cap, (v) => {
        flt.cap = v;
        drawControls();
        changed();
      }),
    );
    clear(
      ranges,
      h("label", null, "Price"),
      input("pmin", "min"),
      "–",
      input("pmax", "max"),
      h("label", null, "%Chg"),
      input("chgmin", "min"),
      "–",
      input("chgmax", "max"),
      h("label", null, "Volume ≥"),
      input("vmin", "shares", "10ch"),
      h("span", { class: "sep" }),
      h("label", null, "Sort"),
      sortSel,
      button(flt.dir === "desc" ? "▼ desc" : "▲ asc", () => {
        flt.dir = flt.dir === "desc" ? "asc" : "desc";
        drawControls();
        changed();
      }),
      h("label", null, "Show"),
      limitSel,
      button("Reset", () => {
        Object.assign(flt, DEFAULTS);
        drawControls();
        changed();
      }),
    );
  };

  /// The dropdowns' values first, until that has worked once; then the screen.
  const refresh = async () => {
    if (!fields) {
      try {
        fields = (await get<Fields>("eqs/fields", {}, ctx.signal)).data;
      } catch (e) {
        if (!ctx.signal.aborted) clear(counts, errorBox(e));
        return;
      }
      // Saved values the listing no longer has would be rejected.
      const has = (list: string[], v: string) => !v || list.includes(v);
      if (!has(fields.sectors.map((s) => s[0]), flt.sector)) flt.sector = "";
      if (!has(fields.industries.map((s) => s[1]), flt.industry)) flt.industry = "";
      if (!has(fields.countries.map((s) => s[0]), flt.country)) flt.country = "";
      drawControls();
    }
    await query();
  };
  refresh();
  ctx.panel.every(300_000, refresh);
}

// --- RV ---------------------------------------------------------------------

interface Peers {
  source: "nasdaq" | "yahoo";
  symbol: string;
  sector?: string;
  industry?: string;
  note?: string;
  peers: { symbol: string; name?: string; mcap?: number }[];
}

interface PeerRow {
  symbol: string;
  name?: string;
  q?: Quote;
  /// The median footer row.
  median?: Partial<Record<Metric, number>>;
}

type Metric = "marketCap" | "trailingPE" | "forwardPE" | "priceToBook" | "dividendYield" | "fiftyTwoWeekChangePercent" | "regularMarketChangePercent";

function median(v: (number | undefined)[]): number | undefined {
  const s = v.filter((x): x is number => typeof x === "number" && isFinite(x)).sort((a, b) => a - b);
  if (!s.length) return undefined;
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

function runRv(ctx: Ctx): void {
  const sym = ctx.sec!;
  const head = h("div", { class: "pad" }, loading("Finding peers"));
  const vsLine = h("div", { class: "pad dim" });
  const metric = (m: Metric) => (r: PeerRow) => (r.median ? r.median[m] : r.q?.[m]);
  const t = new DataTable<PeerRow>(
    [
      { label: "Ticker", get: (r) => (r.median ? "Median" : r.symbol), cls: () => "amber" },
      { label: "Name", get: (r) => (r.median ? "peers, excluding " + sym : (r.name ?? r.q?.longName ?? r.q?.shortName ?? "")), cls: () => "name" },
      { label: "Mkt cap", get: metric("marketCap"), fmt: (v) => f.big(v), num: true },
      { label: "Last", get: (r) => r.q?.regularMarketPrice, fmt: (v, r) => f.price(v, r.q?.priceHint), num: true },
      { label: "%Chg", get: metric("regularMarketChangePercent"), fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true },
      { label: "P/E", get: metric("trailingPE"), fmt: (v) => f.fixedN(v, 1), num: true, title: "Trailing twelve months" },
      { label: "Fwd P/E", get: metric("forwardPE"), fmt: (v) => f.fixedN(v, 1), num: true },
      { label: "P/B", get: metric("priceToBook"), fmt: (v) => f.fixedN(v, 2), num: true },
      { label: "Div yld", get: metric("dividendYield"), fmt: (v) => (f.num(v) ? `${f.fixedN(v, 2)}%` : f.NA), num: true },
      { label: "52w chg", get: metric("fiftyTwoWeekChangePercent"), fmt: (v) => f.pct(v, 1), cls: (v) => f.cls(v), num: true },
    ],
    {
      panel: ctx.panel,
      key: (r) => r.symbol,
      action: (r) => (r.median ? null : `${r.symbol} DES`),
      rowClass: (r) => (r.symbol === sym && !r.median ? "self" : undefined),
    },
  );
  clear(ctx.body, head, t.el, vsLine);

  let peers: Peers | null = null;
  const loadQuotes = async () => {
    if (!peers) return;
    const r = await quotes(
      peers.peers.map((p) => p.symbol),
      ctx.signal,
    );
    if (ctx.signal.aborted) return;
    const bySym = new Map(r.data.map((q) => [q.symbol, q]));
    const rows: PeerRow[] = peers.peers.map((p) => ({ symbol: p.symbol, name: p.name, q: bySym.get(p.symbol) }));
    const others = rows.filter((x) => x.symbol !== sym && x.q);
    const metrics: Metric[] = ["marketCap", "trailingPE", "forwardPE", "priceToBook", "dividendYield", "fiftyTwoWeekChangePercent", "regularMarketChangePercent"];
    // Yahoo omits the yield of non-payers; they count as zero in the median.
    // Negative multiples (losses, negative book) mean nothing there.
    const multiples: Metric[] = ["trailingPE", "forwardPE", "priceToBook"];
    const value = (q: Quote, m: Metric) => {
      const v = q[m];
      if (m === "dividendYield") return v ?? 0;
      return multiples.includes(m) && (v ?? 0) <= 0 ? undefined : v;
    };
    const med = Object.fromEntries(metrics.map((m) => [m, median(others.map((x) => value(x.q!, m)))]));
    const enough = others.length > 1;
    t.set(rows, enough ? [{ symbol: "", median: med }] : []);

    // How the loaded security compares with the peer median.
    const me = bySym.get(sym);
    const vs = (label: string, m: Metric) => {
      const a = f.num(me?.[m]);
      const b = f.num(med[m]);
      if (a === undefined || b === undefined || a <= 0 || b <= 0) return null;
      const prem = (a / b - 1) * 100;
      const word = prem >= 0 ? "premium" : "discount";
      return h("span", { style: { marginRight: "16px" } }, `${label} ${f.fixedN(a, 1)} vs ${f.fixedN(b, 1)}, ${word} ${f.fixedN(Math.abs(prem), 0)}%`);
    };
    const items = enough ? [vs("P/E", "trailingPE"), vs("Fwd P/E", "forwardPE"), vs("P/B", "priceToBook")].filter((x) => x !== null) : [];
    clear(vsLine, items.length ? h("span", { class: "amber", style: { marginRight: "12px" } }, `${sym} vs peer median`) : null, ...items);
    ctx.panel.status(`${peers.source === "nasdaq" ? "Peers: Nasdaq listing" : "Peers: Yahoo"} · multiples: Yahoo · refresh 60s`, r.meta);
  };

  (async () => {
    try {
      const r = await get<Peers>("rv", { s: sym }, ctx.signal);
      peers = r.data;
    } catch (e) {
      if (!ctx.signal.aborted) clear(head, errorBox(e));
      return;
    }
    if (peers.peers.length < 2) {
      clear(head, errorBox(`No peers found for ${sym}${peers.note ? ` (${peers.note})` : ""}`));
      return;
    }
    clear(
      head,
      peers.source === "nasdaq"
        ? h(
            "span",
            null,
            h("span", { class: "amber" }, peers.industry ?? ""),
            h("span", { class: "dim" }, ` · ${peers.sector ?? ""} · `),
            `${peers.peers.length - 1} companies nearest in market cap, by Nasdaq's classification`,
          )
        : h("span", null, h("span", { class: "yellow" }, "People also watch (Yahoo) "), h("span", { class: "dim" }, `· ${peers.note ?? ""}`)),
    );
    await loadQuotes().catch((e) => clear(head, errorBox(e)));
  })();
  ctx.panel.every(60_000, loadQuotes);
}

register(
  {
    code: "EQS",
    name: "Equity Screener",
    group: "Markets",
    needsSec: false,
    help: "Screen all US-listed stocks by sector, industry, market cap, price, % change, volume and country.",
    run: runEqs,
  },
  {
    code: "RV",
    name: "Relative Valuation",
    group: "Equity",
    needsSec: true,
    help: "Peers in the same industry nearest in market cap, with P/E, P/B, yield and returns against the median.",
    run: runRv,
  },
);
